import { Box, Columns } from 'modules/af_cm_ui_kit';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { StoreReadonlyField } from 'modules/cm/fields/EditableField';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import { Props } from 'modules/cm/pages/CaseDetailsPage/subPages/Admin/Admin.types';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import useYupValidationResolver from '../../../../../../form/yup/reactHookFormResolver';
import CheckDirty from '../../../../components/CheckDirty/CheckDirty';
import { saveCase } from '../../../../store/cases/actions';
import { updateAdmin } from '../../../../store/cases/update/actions';
import * as subPages from '../../../../store/subPages';
import { SUBPAGES } from '../../../../store/subPages/types';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';
import { SubPageTemplate } from '../../partials';
import validationSchema from './validationSchema';

const CHANGE_ORIGINATION_ID_CONFIRM_MESSAGE = 'Are you sure you want to change the Originator ID?';

const Admin: React.FC<Props> = ({ modulePrefix, resources, data, initialData, caseId }) => {
  const dispatch = useDispatch();
  const { getErrors } = usePepperError('contact');
  const { getErrors: getAdminErrors } = usePepperError('contact', 'admin');

  const methods = useForm<Props['data']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  useEffect(() => {
    const { reset } = methods;

    reset(data, {
      keepDefaultValues: true,
    });
  }, [data, methods]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateAdmin(methods.getValues()));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateAdmin(methods.getValues()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubPageTemplate title="Admin">
      <FormProvider<Props['data']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(subPages.actions.updateSubPageIsDirty(SUBPAGES.ADMIN, isDirty));
          }}
        />
        <form onSubmit={() => { }}>
          <Columns spacing={4}>
            <Box title="Broker details">
              <UpdatableField getErrors={getErrors} fieldType={INPUTS.TEXT} field={fields[FIELDS.ADVISER_NAME]} />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                field={fields[FIELDS.ADVISER_PHONE]}
                variant={VARIANTS_TYPES.PHONE}
              />
              <UpdatableField getErrors={getErrors} fieldType={INPUTS.TEXT} field={fields[FIELDS.ADVISER_EMAIL]} />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                field={fields[FIELDS.FIRM_FCA_NUMBER]}
                variant={VARIANTS_TYPES.NUMBER}
              />
              <UpdatableField getErrors={getErrors} fieldType={INPUTS.TEXT} field={fields[FIELDS.FIRM_NAME]} />
              <UpdatableField getErrors={getErrors} fieldType={INPUTS.TEXT} field={fields[FIELDS.FIRM_ADDRESS]} />
              <UpdatableField getErrors={getErrors} fieldType={INPUTS.TEXT} field={fields[FIELDS.NETWORK_FCA_NUMBER]} />
              <StoreReadonlyField field={fields[FIELDS.NETWORK_NAME]} />
              <StoreReadonlyField field={fields[FIELDS.CLUB_NAME]} />
              <StoreReadonlyField field={fields[FIELDS.SUBMISSION_ROUTE]} />
              <StoreReadonlyField field={fields[FIELDS.COMMISSION_PAID_TO]} />
              <StoreReadonlyField
                field={fields[FIELDS.STANDARD_COMMISSION_TERMS]}
                variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE}
              />
              <StoreReadonlyField
                field={fields[FIELDS.ENHANCED_COMMISSION_TERMS]}
                variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE}
              />
              <StoreReadonlyField
                field={fields[FIELDS.ONGOING_COMMISSION_TERMS_UPFRONT_FEE]}
                variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE}
              />
              <StoreReadonlyField
                field={fields[FIELDS.ONGOING_COMMISSION_TERMS_ONGOING_FEE]}
                variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE}
              />
              <StoreReadonlyField field={fields[FIELDS.UPFRONT_COMMISSION_AMOUNT]} variant={VARIANTS_TYPES.CURRENCY} />
              <StoreReadonlyField field={fields[FIELDS.ONGOING_COMMISSION_AMOUNT]} variant={VARIANTS_TYPES.CURRENCY} />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                field={fields[FIELDS.ADVISER_FEE]}
                variant={VARIANTS_TYPES.CURRENCY}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.SELECT}
                field={fields[FIELDS.ADVISER_FEE_LAST_PAYMENT_MOMENT]}
                options={resources.creditFeePayMoment}
                required
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                field={fields[FIELDS.PACKAGER_FEE]}
                variant={VARIANTS_TYPES.CURRENCY}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.SELECT}
                field={fields[FIELDS.PACKAGER_FEE_LAST_PAYMENT_MOMENT]}
                options={resources.creditFeePayMoment}
                required
              />
            </Box>
            <>
              <Box title="Admin">
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_OFFER_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_COT_REC_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_FUNDS_REQ_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_EXPECTED_COMPLETION_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_COMPLETION_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_DECLINE_DATE]}
                  variant={VARIANTS_TYPES.DATE}
                />
                <StoreReadonlyField field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_PEPPER_UPLOAD_DATE]} />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_PEPPER_ACCOUNT_ID]}
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_ORIGINATOR_ID]}
                  options={resources.originatorIds}
                  confirm={{
                    text: CHANGE_ORIGINATION_ID_CONFIRM_MESSAGE,
                  }}
                  required
                />
                <UpdatableField
                  getErrors={getAdminErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_SWAP_COST]}
                  variant={VARIANTS_TYPES.DECIMAL_NUMBER}
                />
                <StoreReadonlyField
                  field={fields[FIELDS.ADMIN_PREFIX]()[FIELDS.ADMIN_EXPECTED_MATURITY]}
                  variant={VARIANTS_TYPES.TEXT}
                />
              </Box>
              <Box title="Conveyancing">
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_NAME]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_EMAIL]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_ADDRESS_POSTCODE]}
                  variant={VARIANTS_TYPES.POSTCODE}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_FLAT_NUMBER]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_BUILDING_NUMBER]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_BUILDING_NAME]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_ADDRESS_CITY]}
                  variant={VARIANTS_TYPES.TEXT}
                />
                <UpdatableField
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOLICITOR_PREFIX]()[FIELDS.SOLICITOR_CONTACT_NUMBER]}
                  variant={VARIANTS_TYPES.PHONE}
                />
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>
    </SubPageTemplate>
  );
};

export default Admin;
